import { useState } from 'react'

import { CheckIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  FormControl,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  useDisclosure,
} from '@chakra-ui/react'

import { useIsAdmin, useTeam, useUpdateTeam } from '@/react-query/team'
import { PencilSvg } from '@/views/assets/PencilSvg'

export const DeleteOldInterviews = () => {
  const team = useTeam().data
  const updateTeam = useUpdateTeam()

  const { isOpen, onOpen, onClose } = useDisclosure()

  const [oldInterviewsDays, setOldInterviewsDays] = useState(
    team?.oldInterviewsDays || 30,
  )
  const [isEditingOldInterviewsDays, setIsEditingOldInterviewsDays] =
    useState(false)

  const isAdmin = useIsAdmin()
  if (!isAdmin) {
    return null
  }

  return (
    <>
      <FormControl display={'flex'} alignItems={'center'}>
        <Switch
          id="enable-delete-old-interviews"
          onChange={(event) => {
            event.preventDefault()
            const value = event.target.checked
            if (value) {
              onOpen()
            } else {
              updateTeam.mutate({ deleteOldInterviews: value })
            }
          }}
          isChecked={team?.deleteOldInterviews || false}
          size={'lg'}
          variant={'brand-black'}
        />
        <Box mt={-2} mb="0" ml={'4'} w={'100%'} fontWeight={500}>
          <Text display={'inline'}>Automatically delete interviews after</Text>
          <Flex
            alignItems="center"
            display={'inline-flex'}
            fontWeight={600}
            mx={2}
          >
            {isEditingOldInterviewsDays ? (
              <Input
                w={'80px'}
                mr={2}
                display={'inline'}
                value={oldInterviewsDays}
                onChange={(e) => {
                  e.preventDefault()
                  const value = e.target.value?.replace(/[^0-9]/g, '')
                  if (value === '') {
                    setOldInterviewsDays(0)
                  } else {
                    const parsedValue = parseInt(value)
                    if (parsedValue > 99999) {
                      setOldInterviewsDays(99999)
                    } else {
                      setOldInterviewsDays(parsedValue)
                    }
                  }
                }}
                variant={'brand-solid-gray'}
              />
            ) : (
              <Text ml={4} mr={4}>
                {oldInterviewsDays}
              </Text>
            )}
            <IconButton
              onClick={async (e) => {
                e.preventDefault()
                if (isEditingOldInterviewsDays) {
                  updateTeam.mutate({
                    oldInterviewsDays,
                  })
                }
                setIsEditingOldInterviewsDays((isEditing) => !isEditing)
              }}
              ml={isEditingOldInterviewsDays ? 0 : '1.5px'}
              variant="brand-solid-gray"
              icon={isEditingOldInterviewsDays ? <CheckIcon /> : <PencilSvg />}
              aria-label="Edit old interviews days"
            />
          </Flex>
          <Text display={'inline'}>days</Text>
        </Box>
      </FormControl>
      {isOpen && (
        <ConfirmEnableDeleteOldInterviews
          onClose={onClose}
          oldInterviewsDays={oldInterviewsDays}
        />
      )}
    </>
  )
}

const ConfirmEnableDeleteOldInterviews = ({
  onClose,
  oldInterviewsDays,
}: {
  onClose: () => void
  oldInterviewsDays: number
}) => {
  const updateTeam = useUpdateTeam()

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Confirm</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>
            Are you sure you want to enable the deletion of old interviews?
            <br />
            <br />
            This will automatically delete all interviews that are older than{' '}
            {oldInterviewsDays} days, including existing ones.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={() => {
              updateTeam.mutate({
                oldInterviewsDays,
                deleteOldInterviews: true,
              })
              onClose()
            }}
            variant="brand-solid"
          >
            Enable
          </Button>
          <Button
            variant="brand-solid-white"
            onClick={() => {
              onClose()
            }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
